import type { ReceiptPayload } from './types';
import type { PrinterModelId } from './printerModelConfig';
import { PRINTER_MODEL_PRESETS } from './printerModelConfig';
import { formatThermalMoney } from './formatThermalMoney';
import { itemDetailLines, separatorDashes } from './receiptLayout';

export { getReceiptLineWidthForModel, getReceiptRasterDotsPerLine } from './receiptLayout';

/** Approximate paper width for on-screen preview (58 mm vs 80 mm class). */
export function getReceiptPreviewMaxWidth(modelId: PrinterModelId): string {
  switch (modelId) {
    case 'xp-p203a':
      return '18rem';
    case 'xp-p801a':
    case 'xp-p810':
    case 'xp-q838l':
    case 'xp-80t':
      return '24rem';
    default:
      return '18rem';
  }
}

function padLine(left: string, right: string, width: number): string {
  const maxLeft = Math.max(4, width - right.length - 1);
  let l = left;
  if (l.length > maxLeft) l = `${l.slice(0, maxLeft - 1)}…`;
  const spaces = Math.max(1, width - l.length - right.length);
  return `${l}${' '.repeat(spaces)}${right}`;
}

export type ReceiptPreviewLine =
  | { kind: 'logo'; url: string }
  | { kind: 'branding'; text: string }
  | { kind: 'title'; text: string }
  | { kind: 'text'; text: string }
  | { kind: 'meta'; text: string }
  | { kind: 'item'; text: string }
  | { kind: 'total'; text: string }
  | { kind: 'qr'; text: string }
  | { kind: 'spacer' };

/**
 * Visual layout lines for UI preview (mirrors encodeReceipt structure, not byte-for-byte).
 */
export function layoutReceiptForPreview(
  r: ReceiptPayload,
  lineWidth: number,
): ReceiptPreviewLine[] {
  const currency = r.currency || 'LKR';
  const out: ReceiptPreviewLine[] = [];

  if (r.brandingHeader?.logoUrl) {
    out.push({ kind: 'logo', url: r.brandingHeader.logoUrl });
  }
  if (r.brandingHeader?.lines?.length) {
    for (const line of r.brandingHeader.lines) {
      const trimmed = line.trim();
      if (trimmed) out.push({ kind: 'branding', text: trimmed });
    }
    out.push({ kind: 'text', text: separatorDashes(lineWidth) });
    out.push({ kind: 'spacer' });
  }

  out.push({ kind: 'title', text: r.title ?? 'RECEIPT' });

  if (r.headerLines?.length) {
    for (const line of r.headerLines) {
      out.push({ kind: 'text', text: line });
    }
    out.push({ kind: 'spacer' });
  }

  if (r.meta?.length) {
    for (const row of r.meta) {
      out.push({ kind: 'meta', text: padLine(row.left, row.right, lineWidth) });
    }
    out.push({ kind: 'spacer' });
  }

  if (r.items?.length) {
    for (const item of r.items) {
      const left = item.qty != null ? `${item.label} x${item.qty}` : item.label;
      const right =
        item.total != null
          ? formatThermalMoney(item.total, currency)
          : item.unitPrice != null
            ? formatThermalMoney(item.unitPrice, currency)
            : '';
      out.push({ kind: 'item', text: padLine(left, right, lineWidth) });
      for (const detail of itemDetailLines(item.details, lineWidth)) out.push({ kind: 'text', text: detail });
    }
    out.push({ kind: 'spacer' });
  }

  if (r.subtotal != null) {
    out.push({ kind: 'meta', text: padLine('Subtotal', formatThermalMoney(r.subtotal, currency), lineWidth) });
  }
  if (r.tax != null) {
    out.push({ kind: 'meta', text: padLine('Tax', formatThermalMoney(r.tax, currency), lineWidth) });
  }

  out.push({ kind: 'total', text: padLine('TOTAL', formatThermalMoney(r.total, currency), lineWidth) });
  out.push({ kind: 'spacer' });

  if (r.footerLines?.length) {
    for (const line of r.footerLines) {
      out.push({ kind: 'text', text: line });
    }
  }

  if (r.printedAt) {
    out.push({ kind: 'text', text: r.printedAt });
  }

  if (r.qrPayload?.trim()) {
    out.push({ kind: 'spacer' });
    out.push({ kind: 'qr', text: r.qrPayload.trim() });
  }

  return out;
}

export function getPrinterModelDisplayName(modelId: PrinterModelId, t: (key: string) => string): string {
  const key = `thermalPrinterHelp.printerModel.models.${modelId}`;
  const translated = t(key);
  if (translated && translated !== key) return translated;
  return PRINTER_MODEL_PRESETS[modelId]?.id ?? modelId;
}
